import { SvgIconComponent } from "@mui/icons-material";
import * as MuiIcons from "@mui/icons-material";
import { forwardRef } from "react";
import { twMerge } from "tailwind-merge";

import { InputWrapper } from "@/components/global/form/inputs/input-wrapper";
import {
  BaseInputStyleProps,
  baseInputStylesMap,
  defaultBaseInputStyles,
} from "@/components/global/form/inputs/styles/base-input.styles";
import { InputWrapperStyleProps } from "@/components/global/form/inputs/styles/input-wrapper.styles";
import Icon from "@/components/icon";

interface IconConfig {
  name: keyof typeof MuiIcons;
  position?: "left" | "right";
  size?: number;
  className?: string;
  onClick?: () => void;
  cursorOnHover?: "pointer" | "default" | "not-allowed";
}

interface InputEvents {
  onClick?: () => void;
  onFocus?: () => void;
  onBlur?: () => void;
}

interface TextInputProps {
  error?: string;
  isValid?: boolean;
  required?: boolean;
  disabled?: boolean;
  dataCy?: string;
  placeholder?: string;
  wrapperStyles?: InputWrapperStyleProps;
  styles?: BaseInputStyleProps;
  events?: InputEvents;
  cursorOnHover?: "pointer" | "default" | "not-allowed";
  readOnly?: boolean;
  icon?: IconConfig;
  [key: string]: any;
}

const getCursorClass = (cursor?: "pointer" | "default" | "not-allowed") => {
  switch (cursor) {
    case "pointer":
      return "hover:cursor-pointer";
    case "default":
      return "hover:cursor-default";
    case "not-allowed":
      return "hover:cursor-not-allowed";
    default:
      return "";
  }
};

/**
 * Text input field component with validation states
 * @param {string} [error] - Error message to display
 * @param {boolean} [isValid] - Whether the input is valid
 * @param {boolean} [required] - Whether the input is required
 * @param {boolean} [disabled] - Whether the input is disabled
 * @param {string} [dataCy] - Data-cy attribute for testing
 * @param {string} [placeholder] - Input placeholder text
 * @param {InputWrapperStyleProps} [wrapperStyles] - Styles for the input wrapper
 * @param {TextInputStyleProps} [styles] - Custom styles for the input
 * @param {InputEvents} [events] - Events for the input
 * @param {IconConfig} [icon] - Icon configuration for the input
 * @param {string} [icon.name] - Icon name
 * @param {string} [icon.position] - Icon position
 * @param {number} [icon.size] - Icon size
 * @param {string} [icon.className] - Icon class name
 * @param {function} [icon.onClick] - Icon click handler
 * @param {string} [icon.cursorOnHover] - Cursor style on hover
 * @example
 * <TextInput
 *   placeholder="Enter your email"
 *   error={errorMessage}
 *   isValid={isValid}
 *   styles={{
 *     textColor: "primary",
 *     placeholderStyle: "italic",
 *     padding: { left: "md", right: "lg" }
 *   }}
 *   wrapperStyles={{
 *     height: "md",
 *     rounded: "lg"
 *   }}
 * />
 */
export const TextInput = forwardRef<HTMLInputElement, TextInputProps>(
  (
    {
      error,
      isValid,
      required: _required,
      disabled,
      dataCy,
      styles,
      wrapperStyles,
      events,
      cursorOnHover,
      readOnly,
      icon,
      ...props
    }: TextInputProps,
    ref,
  ) => {
    const mergedStyles = { ...defaultBaseInputStyles, ...styles };

    // Dynamically get the MUI icon component
    const IconComponent = icon?.name
      ? (MuiIcons[icon.name] as SvgIconComponent)
      : null;

    return (
      <InputWrapper error={error} disabled={disabled} styles={wrapperStyles}>
        <div
          className={twMerge(
            "relative flex items-center w-full",
            getCursorClass(cursorOnHover),
          )}
        >
          {IconComponent && icon?.position === "left" && (
            <IconComponent
              className={twMerge(
                "absolute left-3",
                error && "text-primary",
                icon.className,
              )}
              sx={{ fontSize: icon.size || 24 }}
            />
          )}
          <input
            ref={ref}
            type="text"
            className={twMerge(
              "w-full bg-transparent autofill:bg-transparent rounded-md",
              mergedStyles.placeholderStyle === "italic" &&
                "placeholder:italic",
              baseInputStylesMap.padding[mergedStyles.padding?.left || "md"],
              baseInputStylesMap.padding[mergedStyles.padding?.right || "lg"],
              error && `text-${mergedStyles.textColor}`,
              disabled && "cursor-not-allowed",
              cursorOnHover && `hover:cursor-${cursorOnHover}`,
              icon?.position === "left" && "pl-12",
              icon?.position === "right" && "pr-12",
              mergedStyles.className,
            )}
            disabled={disabled}
            readOnly={readOnly}
            data-cy={dataCy}
            onClick={events?.onClick}
            onFocus={events?.onFocus}
            onBlur={events?.onBlur}
            {...props}
          />
          {IconComponent && icon?.position === "right" && (
            <IconComponent
              className={twMerge(
                "absolute right-3",
                error && "text-primary",
                icon.className,
              )}
              sx={{ fontSize: icon.size || 24 }}
              onClick={icon.onClick}
            />
          )}
          <div className="flex items-center gap-2 absolute right-2">
            {error && <Icon icon="cross" size={25} className="text-primary" />}
            {isValid && !error && (
              <Icon icon="check-simple" size={25} className="text-success2" />
            )}
          </div>
        </div>
      </InputWrapper>
    );
  },
);

TextInput.displayName = "TextInput";
